import { Autocomplete, Box, TextField } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

export type SelectOption = {
  id: string | number;
  name: string;
};

interface SelectFieldProps {
  name: string;
  label: string;
  data: { items: SelectOption[] } | undefined;
  isLoading?: boolean;
  required?: boolean;
  disabled?: boolean;
  optionEqualToValue?: (option: SelectOption, value: SelectOption) => boolean;
  defaultValue?: SelectOption;
}

const placeholder = { name: '', id: '' };

const SelectField = ({
  name,
  label,
  data,
  isLoading = false,
  required = false,
  disabled = false,
  optionEqualToValue = undefined,
  defaultValue = undefined,
}: SelectFieldProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = errors[name];

  const getValue = useMemo(() => {
    return (v: string | number) => data?.items.find((item) => item.id == v) ?? null;
  }, [data?.items]);

  // Handle default value updates
  const { setValue, getValues } = useFormContext();

  useEffect(() => {
    const currentValue = getValues(name);
    if (
      defaultValue &&
      (currentValue === undefined || currentValue === null || currentValue === '')
    ) {
      setValue(name, defaultValue.id, { shouldValidate: true, shouldDirty: true });
    }
  }, [defaultValue, name, setValue, getValues]);

  return (
    <Box className='flex flex-row items-center justify-center w-full py-8'>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => {
          const err = error?.message as string;

          return (
            <Autocomplete
              value={isLoading ? placeholder : getValue(value)}
              onChange={(_event, newValue) => onChange(newValue?.id ?? null)}
              options={data?.items ?? []}
              getOptionLabel={(option) => option.name}
              defaultValue={defaultValue}
              isOptionEqualToValue={
                optionEqualToValue ?? ((option, value) => option?.id === value?.id)
              }
              loading={isLoading}
              disabled={disabled}
              className='w-full'
              renderInput={(params) => (
                <TextField
                  {...params}
                  key={`${name}-${params.id}`}
                  label={label}
                  error={!!error}
                  variant='outlined'
                  size='small'
                  className='w-full'
                  helperText={err}
                  required={required}
                />
              )}
            />
          );
        }}
      />
    </Box>
  );
};

export default memo(SelectField);
